import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useXRInputSourceStateContext } from "@react-three/xr";
import {
  AdditiveBlending,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Group,
  MeshBasicMaterial,
  PointLight,
  Vector3,
} from "three";
import { BladeData, registerBlade, unregisterBlade } from "../game/collision";
import { useGameStore } from "../game/useGameStore";
import { MIN_SLASH_SPEED, SNAP_WINDOW_SAMPLES } from "../game/constants";
import { BladeTrail } from "./BladeTrail";
import { colors } from "../ui/theme";
import {
  createKatanaBladeGeometry,
  SORI_RISE,
  bladeEdgeYAt,
  bladeLocalZAt,
  bladeRidgeYAt,
  bladeWidthScaleAt,
} from "./bladeGeometry";
import {
  createBladeFinishTexture,
  createHabakiTexture,
  createTsubaPatinaTexture,
  createTsukaWrapTexture,
} from "./hiltTextures";
import { getKatanaEnvMap } from "./katanaEnvMap";

// ─── Hilt layout (metres, group-local Z along the blade axis) ────────────────
// See .extras/design/HANDOFF-katana-3d-build.md — nagasa ~68cm, tsuka ~1/4-1/3
// of total length. Anchored so the tsuba/guard sits near the group origin
// (the controller grip pose), matching "grip origin at the tsuka, not the
// mesh centroid" — the hand holds behind this point, the blade extends ahead.
// First pass (boost 4.0, decay 0.25s) read as no effect at all — too small
// next to the blade's other per-hit VFX (particle burst, hemisphere flash,
// hit-flash pool light). Raised well past the combo aura's own max (3.5,
// see auraIntensity below) so it visibly dominates the blade for a beat.
const TENOUCHI_FLASH_DECAY_SECONDS = 0.4;
const TENOUCHI_FLASH_BOOST = 14.0;
// Sharp follow-up jolt chained right after the grade pulse on a tenouchi hit
// — reads as the squeeze/snap itself, not just a stronger version of the
// same pulse. A single short pulse chained onto the base one (first attempt)
// was imperceptible — too brief and too close to the base pulse to read as
// distinct. Now a deliberate double-tap: two full-intensity pulses with a
// clear gap between them, long enough each to actually register.
const TENOUCHI_SNAP_PULSE_INTENSITY = 1.0;
const TENOUCHI_SNAP_PULSE_DURATION = 90;
const TENOUCHI_SNAP_PULSE_GAP_MS = 70;

const BLADE_ORIGIN_Z = -0.012;
const TIP_LOCAL_Z = BLADE_ORIGIN_Z + bladeLocalZAt(1);
const TRAIL_BASE_T = 0.08;
const TRAIL_BASE_Z = BLADE_ORIGIN_Z + bladeLocalZAt(TRAIL_BASE_T);
const TRAIL_EDGE_CLEARANCE_BASE = 0.0035;
const TRAIL_EDGE_CLEARANCE_TIP = 0.0015;
const TRAIL_EDGE_SAMPLE_T = [
  1, 0.91, 0.8, 0.68, 0.54, 0.39, 0.23, 0.08,
] as const;

const HABAKI_LENGTH = 0.032;
const HABAKI_Z = BLADE_ORIGIN_Z - HABAKI_LENGTH / 2 + 0.004;

const TSUBA_Z = 0.002;
const TSUBA_RADIUS = 0.058;
const TSUBA_THICKNESS = 0.013;

const FUCHI_Z = TSUBA_Z + TSUBA_THICKNESS / 2 + 0.008;

const HANDLE_LENGTH = 0.24; // ~26% of total (blade + handle) — within the 1/4-1/3 target
const HANDLE_Z_START = FUCHI_Z + 0.01;
const HANDLE_Z = HANDLE_Z_START + HANDLE_LENGTH / 2;

const KASHIRA_Z = HANDLE_Z_START + HANDLE_LENGTH + 0.012;

const MENUKI_Z = HANDLE_Z_START + HANDLE_LENGTH * 0.38;
const MENUKI_ANGLE = 0.55; // radians around the handle axis

const EDGE_GLOW_SEGMENTS = 18;
const EDGE_GLOW_YOKOTE_T = 0.86;
const EDGE_GLOW_START_T = 0.055;

// Tsuka cross-section is oval, not round (real handles are): full height in
// the cut plane (ha–mune, world Y), narrowed side-to-side. Applied as a
// local-X scale on the handle meshes (local X survives the [π/2,0,0]
// rotation unchanged).
const TSUKA_OVAL = 0.8;

// ─── Light balance (tuned together — see HANDOFF-katana-3d-build.md §4) ─────
// With the env map now feeding real reflections into the steel, the emissive
// and the additive compensation strips both step down a notch from their
// pre-envmap values; the strips remain as the fast-motion readability floor
// and the bloom feed. Final on-device (Quest) calibration pending, per the
// project's generous-then-tighten methodology.
const BLADE_ENV_INTENSITY = 1.25;
const HILT_ENV_INTENSITY = 0.5;
const HABAKI_ENV_INTENSITY = 0.7;
const MENUKI_ENV_INTENSITY = 0.8;
const BLADE_EMISSIVE_BLOOM = 0.78; // was 0.9 pre-envmap
const BLADE_EMISSIVE_GLOW = 0.7; // was 0.82
const BLADE_EMISSIVE_PLAIN = 0.62; // was 0.72
const SHINOGI_OPACITY_BASE = 0.2; // was 0.28
const SHINOGI_OPACITY_MOTION = 0.16; // was 0.18
const HAMON_OPACITY_BASE = 0.06; // was 0.08
const HAMON_OPACITY_MOTION = 0.04; // was 0.05
const ANDON_OPACITY_BASE = 0.018;
const ANDON_OPACITY_MOTION = 0.012;

function trailEdgeYAt(t: number) {
  const offset =
    TRAIL_EDGE_CLEARANCE_TIP +
    (TRAIL_EDGE_CLEARANCE_BASE - TRAIL_EDGE_CLEARANCE_TIP) * (1 - t);
  return bladeEdgeYAt(t) - offset;
}

function bladeSceneZAt(t: number) {
  return BLADE_ORIGIN_Z + bladeLocalZAt(t);
}

function createBladeLightStripGeometry(
  halfWidth: number,
  yAt: (t: number) => number,
  {
    startT = EDGE_GLOW_START_T,
    endT = 1,
    taper = 0.45,
  }: { startT?: number; endT?: number; taper?: number } = {},
) {
  const vertCount = (EDGE_GLOW_SEGMENTS + 1) * 2;
  const positions = new Float32Array(vertCount * 3);
  const indices: number[] = [];

  for (let i = 0; i <= EDGE_GLOW_SEGMENTS; i++) {
    const u = i / EDGE_GLOW_SEGMENTS;
    const t = startT + (endT - startT) * u;
    const widthScale = bladeWidthScaleAt(t);
    const stripY = yAt(t);
    const z = bladeLocalZAt(t);
    const tipFade =
      1 - Math.max(0, t - EDGE_GLOW_YOKOTE_T) / (1 - EDGE_GLOW_YOKOTE_T);
    const stripHalfWidth =
      halfWidth * (0.55 + widthScale * 0.45) * (taper + (1 - taper) * tipFade);
    const left = i * 2;
    const right = left + 1;

    positions[left * 3 + 0] = -stripHalfWidth;
    positions[left * 3 + 1] = stripY;
    positions[left * 3 + 2] = z;
    positions[right * 3 + 0] = stripHalfWidth;
    positions[right * 3 + 1] = stripY;
    positions[right * 3 + 2] = z;

    if (i < EDGE_GLOW_SEGMENTS) {
      const a = left;
      const b = right;
      const c = left + 2;
      const d = right + 2;
      indices.push(a, b, c, b, d, c);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

function createBladeEdgeGlowGeometry(halfWidth: number, edgeOffset: number) {
  return createBladeLightStripGeometry(
    halfWidth,
    (t) => bladeEdgeYAt(t) - edgeOffset,
  );
}

function createBladeShinogiHighlightGeometry() {
  return createBladeLightStripGeometry(
    0.0027,
    (t) => bladeRidgeYAt(t) + 0.0014,
    {
      startT: 0.08,
      endT: 0.94,
      taper: 0.22,
    },
  );
}

function createBladeHamonGlowGeometry() {
  return createBladeLightStripGeometry(
    0.0038,
    (t) => bladeEdgeYAt(t) + 0.0028,
    {
      startT: 0.12,
      endT: 0.88,
      taper: 0.32,
    },
  );
}

function createBladeAndonBounceGeometry() {
  return createBladeLightStripGeometry(0.007, (t) => bladeRidgeYAt(t) - 0.004, {
    startT: 0.04,
    endT: 0.58,
    taper: 0.5,
  });
}

// Habaki as a true wedge — flaring where it meets the tsuba, slimming toward
// the blade — instead of the old plain box, which read as "a box" up close
// and whose 14mm height didn't even wrap the 30mm-tall blade section (the ha
// and mune poked through the collar). Vertex tweak on a BoxGeometry keeps
// the per-face UVs for the yasurime texture; normals recomputed after.
function createHabakiWedgeGeometry() {
  const geometry = new BoxGeometry(0.013, 0.035, HABAKI_LENGTH);
  const position = geometry.attributes.position;
  for (let i = 0; i < position.count; i++) {
    const flare = position.getZ(i) > 0 ? 1.12 : 0.9;
    position.setXY(i, position.getX(i) * flare, position.getY(i) * flare);
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

export function Katana() {
  const state = useXRInputSourceStateContext("controller");
  const hand = state.inputSource.handedness as "left" | "right" | "none";
  const bladeId = `katana-${hand}`;

  const tipRef = useRef<Group | null>(null);
  const baseRef = useRef<Group | null>(null);
  const trailPoint0Ref = useRef<Group | null>(null);
  const trailPoint1Ref = useRef<Group | null>(null);
  const trailPoint2Ref = useRef<Group | null>(null);
  const trailPoint3Ref = useRef<Group | null>(null);
  const trailPoint4Ref = useRef<Group | null>(null);
  const trailPoint5Ref = useRef<Group | null>(null);
  const trailPoint6Ref = useRef<Group | null>(null);
  const trailPoint7Ref = useRef<Group | null>(null);
  const prevTip = useRef(new Vector3());
  const currTip = useRef(new Vector3());
  const primedRef = useRef(false);
  const speedRef = useRef(0);
  // Tenouchi "snap" — speed spike vs. the moving average of the preceding
  // ~SNAP_WINDOW_SAMPLES frames, computed with an incrementally-maintained
  // sum so it's O(1) per frame (no reduce over the buffer, no allocation).
  const speedHistoryRef = useRef(new Float32Array(SNAP_WINDOW_SAMPLES));
  const speedHistoryIndexRef = useRef(0);
  const speedHistorySumRef = useRef(0);
  const speedHistoryCountRef = useRef(0);
  // Tenouchi visual flash — set to 1 on a qualifying hit (pulseImpact),
  // decayed to 0 in useFrame, blended into the combo aura below.
  const tenouchiFlashRef = useRef(0);
  const auraLightRef = useRef<PointLight | null>(null);
  const shinogiMaterialRef = useRef<MeshBasicMaterial | null>(null);
  const hamonMaterialRef = useRef<MeshBasicMaterial | null>(null);
  const andonBounceMaterialRef = useRef<MeshBasicMaterial | null>(null);

  // Registered once and mutated in place every frame — avoids allocating a
  // new object + two Vector3 clones per frame just to hand the same data to
  // collision.ts (which reads it from the shared `blades` map).
  const bladeData = useRef<BladeData>({
    active: false,
    tipPrev: new Vector3(),
    tipCurr: new Vector3(),
    speed: 0,
    snap: 0,
  });

  const wield = useGameStore((s) => s.settings.wield);
  const singleHand = useGameStore((s) => s.settings.singleHand);
  const combo = useGameStore((s) => s.combo);
  const bloomEnabled = useGameStore((s) => s.bloomEnabled);
  const swordGlowEnabled = useGameStore((s) => s.swordGlowEnabled);
  const phase = useGameStore((s) => s.phase);
  const isActive =
    phase !== "demo" && (wield === "dual" || hand === singleHand);

  const bladeEmissiveIntensity = bloomEnabled
    ? BLADE_EMISSIVE_BLOOM
    : swordGlowEnabled
      ? BLADE_EMISSIVE_GLOW
      : BLADE_EMISSIVE_PLAIN;

  // Combo aura — blade glows brighter at high combo
  const auraIntensity =
    combo >= 20 ? 3.5 : combo >= 10 ? 1.8 : combo >= 5 ? 0.7 : 0;
  const auraColor = combo >= 5 ? colors.gold : colors.steel;

  // Built once — a swept BufferGeometry + three small canvas textures. Never
  // regenerated per frame or per render (Milestone 9: no per-frame allocation).
  const bladeGeometry = useMemo(() => createKatanaBladeGeometry(), []);
  const shinogiHighlightGeometry = useMemo(
    () => createBladeShinogiHighlightGeometry(),
    [],
  );
  const hamonGlowGeometry = useMemo(() => createBladeHamonGlowGeometry(), []);
  const andonBounceGeometry = useMemo(
    () => createBladeAndonBounceGeometry(),
    [],
  );
  const edgeGlowOuterGeometry = useMemo(
    () => createBladeEdgeGlowGeometry(0.011, 0.0025),
    [],
  );
  const edgeGlowInnerGeometry = useMemo(
    () => createBladeEdgeGlowGeometry(0.0045, 0.0014),
    [],
  );
  const trailPointRefs = useMemo(
    () => [
      trailPoint0Ref,
      trailPoint1Ref,
      trailPoint2Ref,
      trailPoint3Ref,
      trailPoint4Ref,
      trailPoint5Ref,
      trailPoint6Ref,
      trailPoint7Ref,
    ],
    [],
  );
  const habakiGeometry = useMemo(() => createHabakiWedgeGeometry(), []);
  const tsukaWrapTexture = useMemo(() => createTsukaWrapTexture(), []);
  const tsubaPatinaTexture = useMemo(() => createTsubaPatinaTexture(), []);
  const habakiTexture = useMemo(() => createHabakiTexture(), []);
  const bladeFinishTexture = useMemo(() => createBladeFinishTexture(), []);
  // Shared app-lifetime singleton (both hands' katanas) — never disposed
  // here; see katanaEnvMap.ts.
  const envMap = useMemo(() => getKatanaEnvMap(), []);

  useEffect(() => {
    return () => {
      bladeGeometry.dispose();
      shinogiHighlightGeometry.dispose();
      hamonGlowGeometry.dispose();
      andonBounceGeometry.dispose();
      edgeGlowOuterGeometry.dispose();
      edgeGlowInnerGeometry.dispose();
      habakiGeometry.dispose();
      tsukaWrapTexture.dispose();
      tsubaPatinaTexture.dispose();
      habakiTexture.dispose();
      bladeFinishTexture.dispose();
    };
  }, [
    bladeGeometry,
    shinogiHighlightGeometry,
    hamonGlowGeometry,
    andonBounceGeometry,
    edgeGlowOuterGeometry,
    edgeGlowInnerGeometry,
    habakiGeometry,
    tsukaWrapTexture,
    tsubaPatinaTexture,
    habakiTexture,
    bladeFinishTexture,
  ]);

  useEffect(() => {
    bladeData.current.pulse = pulseImpact;
    registerBlade(bladeId, bladeData.current);
    return () => unregisterBlade(bladeId);
  }, [bladeId]);

  function pulseImpact(grade: string, tenouchi: boolean) {
    // Set before the haptics early-return below so the visual flash still
    // fires on desktop/IWER dev, which has no gamepad haptic actuator.
    if (tenouchi) tenouchiFlashRef.current = 1;

    const actuator = state.inputSource.gamepad?.hapticActuators?.[0];
    if (!actuator?.pulse) return;

    const [intensity, duration] =
      // OBSTACLE: the player's body got hit (Obstacle.tsx, via
      // collision.ts's pulseAllBlades), not a cut-quality grade — a blunt,
      // sharpest jolt, longer than IPPON's, so it reads as "struck" rather
      // than "great cut."
      grade === "OBSTACLE"
        ? [1.0, 140]
        : grade === "IPPON"
          ? [0.95, 90]
          : grade === "PERFECT"
            ? [0.75, 70]
            : grade === "GREAT"
              ? [0.55, 55]
              : grade === "GOOD"
                ? [0.35, 40]
                : grade === "LATE"
                  ? [0.25, 30]
                  : [0.12, 20];

    const basePulse = actuator.pulse(intensity, duration);
    if (tenouchi) {
      basePulse
        .then(
          () =>
            new Promise((resolve) =>
              setTimeout(resolve, TENOUCHI_SNAP_PULSE_GAP_MS),
            ),
        )
        .then(() =>
          actuator.pulse(
            TENOUCHI_SNAP_PULSE_INTENSITY,
            TENOUCHI_SNAP_PULSE_DURATION,
          ),
        )
        .then(
          () =>
            new Promise((resolve) =>
              setTimeout(resolve, TENOUCHI_SNAP_PULSE_GAP_MS),
            ),
        )
        .then(() =>
          actuator.pulse(
            TENOUCHI_SNAP_PULSE_INTENSITY,
            TENOUCHI_SNAP_PULSE_DURATION,
          ),
        )
        .catch(() => {});
    } else {
      basePulse.catch(() => {});
    }
  }

  function resetSpeedHistory() {
    speedHistoryRef.current.fill(0);
    speedHistoryIndexRef.current = 0;
    speedHistorySumRef.current = 0;
    speedHistoryCountRef.current = 0;
  }

  useFrame((_, delta) => {
    const data = bladeData.current;
    if (!tipRef.current) {
      primedRef.current = false;
      speedRef.current = 0;
      resetSpeedHistory();
      data.active = false;
      data.speed = 0;
      data.snap = 0;
      return;
    }

    prevTip.current.copy(currTip.current);
    tipRef.current.getWorldPosition(currTip.current);

    if (!primedRef.current) {
      primedRef.current = true;
      prevTip.current.copy(currTip.current);
      speedRef.current = 0;
      resetSpeedHistory();
      data.active = false;
      data.tipPrev.copy(currTip.current);
      data.tipCurr.copy(currTip.current);
      data.speed = 0;
      data.snap = 0;
      return;
    }

    const speed =
      delta > 0 ? prevTip.current.distanceTo(currTip.current) / delta : 0;

    // Snap = this frame's speed minus the average of the preceding window,
    // computed from the buffer's state *before* inserting the current
    // sample — a sudden spike is measured against where the swing was
    // heading, not diluted by itself.
    const history = speedHistoryRef.current;
    const historyIdx = speedHistoryIndexRef.current;
    const historyCount = speedHistoryCountRef.current;
    const avgSpeed = historyCount > 0 ? speedHistorySumRef.current / historyCount : 0;
    const snap = Math.max(0, speed - avgSpeed);
    speedHistorySumRef.current += speed - history[historyIdx];
    history[historyIdx] = speed;
    speedHistoryIndexRef.current = (historyIdx + 1) % SNAP_WINDOW_SAMPLES;
    if (historyCount < SNAP_WINDOW_SAMPLES) {
      speedHistoryCountRef.current = historyCount + 1;
    }

    speedRef.current = speed;
    const motion = Math.min(1, speed / 5.5);
    if (shinogiMaterialRef.current) {
      shinogiMaterialRef.current.opacity =
        SHINOGI_OPACITY_BASE + motion * SHINOGI_OPACITY_MOTION;
    }
    if (hamonMaterialRef.current) {
      hamonMaterialRef.current.opacity =
        HAMON_OPACITY_BASE + motion * HAMON_OPACITY_MOTION;
    }
    if (andonBounceMaterialRef.current) {
      andonBounceMaterialRef.current.opacity =
        ANDON_OPACITY_BASE + motion * ANDON_OPACITY_MOTION;
    }

    data.active = isActive && speed > MIN_SLASH_SPEED;
    data.tipPrev.copy(prevTip.current);
    data.tipCurr.copy(currTip.current);
    data.speed = speed;
    data.snap = snap;

    // Tenouchi flash decay, blended into the always-mounted combo aura
    // light — reuses that light rather than mounting a new one (see the
    // "Combo aura" comment below for why mount/unmount here is unsafe).
    tenouchiFlashRef.current = Math.max(
      0,
      tenouchiFlashRef.current - delta / TENOUCHI_FLASH_DECAY_SECONDS,
    );
    if (auraLightRef.current) {
      const flash = tenouchiFlashRef.current;
      auraLightRef.current.intensity = auraIntensity + flash * TENOUCHI_FLASH_BOOST;
      auraLightRef.current.color.set(flash > 0.15 ? colors.violet : auraColor);
    }
  });

  if (!isActive) return null;

  return (
    <group rotation={[Math.PI / 6, 0, 0]}>
      {/* Ha/mune/shinogi blade — swept shinogi-zukuri cross-section with sori
          and a tapered kissaki. Emissive so it reads on the dark dōjō
          background and so the scene bloom picks it up. Vertex-colored hamon
          band baked into the geometry (see bladeGeometry.ts). DoubleSide as a
          safety net for sweep winding — normals are flipped per-fragment for
          back faces by the renderer, so lighting stays correct either way. */}
      <mesh geometry={bladeGeometry} position={[0, 0, BLADE_ORIGIN_Z]}>
        {/* Polished steel: the env map (katanaEnvMap.ts) supplies the actual
            reflections — amber andon glow and cool moonlight sweeping along
            the sori as the blade moves. The roughnessMap carves the polish
            zones (mirror ha, misty hamon band, satin ji) into that
            reflection; base roughness is the map's multiplier ceiling. */}
        <meshStandardMaterial
          color={colors.steel}
          emissive="#586068"
          emissiveIntensity={bladeEmissiveIntensity}
          metalness={0.9}
          roughness={0.5}
          roughnessMap={bladeFinishTexture}
          envMap={envMap}
          envMapIntensity={BLADE_ENV_INTENSITY}
          vertexColors
          side={DoubleSide}
        />
      </mesh>

      <mesh
        geometry={shinogiHighlightGeometry}
        position={[0, 0, BLADE_ORIGIN_Z]}
        renderOrder={2}
      >
        <meshBasicMaterial
          ref={shinogiMaterialRef}
          color="#f7fbff"
          transparent
          opacity={SHINOGI_OPACITY_BASE}
          depthWrite={false}
          blending={AdditiveBlending}
          side={DoubleSide}
          toneMapped={false}
        />
      </mesh>

      <mesh
        geometry={hamonGlowGeometry}
        position={[0, 0, BLADE_ORIGIN_Z]}
        renderOrder={2}
      >
        <meshBasicMaterial
          ref={hamonMaterialRef}
          color="#cfe7ff"
          transparent
          opacity={HAMON_OPACITY_BASE}
          depthWrite={false}
          blending={AdditiveBlending}
          side={DoubleSide}
          toneMapped={false}
        />
      </mesh>

      <mesh
        geometry={andonBounceGeometry}
        position={[0, 0, BLADE_ORIGIN_Z]}
        renderOrder={2}
      >
        <meshBasicMaterial
          ref={andonBounceMaterialRef}
          color={colors.amber}
          transparent
          opacity={ANDON_OPACITY_BASE}
          depthWrite={false}
          blending={AdditiveBlending}
          side={DoubleSide}
          toneMapped={false}
        />
      </mesh>

      {swordGlowEnabled && (
        <>
          <mesh
            geometry={edgeGlowOuterGeometry}
            position={[0, 0, BLADE_ORIGIN_Z]}
            renderOrder={3}
          >
            <meshBasicMaterial
              color="#dfefff"
              transparent
              opacity={0.055}
              depthWrite={false}
              blending={AdditiveBlending}
              side={DoubleSide}
              toneMapped={false}
            />
          </mesh>
          <mesh
            geometry={edgeGlowInnerGeometry}
            position={[0, 0, BLADE_ORIGIN_Z]}
            renderOrder={4}
          >
            <meshBasicMaterial
              color={colors.amber}
              transparent
              opacity={0.055}
              depthWrite={false}
              blending={AdditiveBlending}
              side={DoubleSide}
              toneMapped={false}
            />
          </mesh>
        </>
      )}

      {/* Habaki — brass collar at the blade base, diagonal yasurime marks.
          Wedge profile (createHabakiWedgeGeometry) wrapping the full blade
          section, flaring toward the tsuba. */}
      <mesh geometry={habakiGeometry} position={[0, 0, HABAKI_Z]}>
        <meshStandardMaterial
          map={habakiTexture}
          metalness={0.75}
          roughness={0.4}
          envMap={envMap}
          envMapIntensity={HABAKI_ENV_INTENSITY}
        />
      </mesh>

      {/* Tsuba — iron guard, edge-on. Layered: plate + raised mimi rim +
          seppa-dai boss, aged-iron patina texture. The visual anchor of the
          whole weapon, per the handoff doc. */}
      <group position={[0, 0, TSUBA_Z]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry
            args={[TSUBA_RADIUS, TSUBA_RADIUS, TSUBA_THICKNESS, 24]}
          />
          <meshStandardMaterial
            map={tsubaPatinaTexture}
            metalness={0.55}
            roughness={0.6}
            envMap={envMap}
            envMapIntensity={HILT_ENV_INTENSITY}
          />
        </mesh>
        {/* Mimi — raised rim */}
        <mesh>
          <torusGeometry args={[TSUBA_RADIUS, 0.005, 6, 28]} />
          <meshStandardMaterial
            color="#15150f"
            metalness={0.6}
            roughness={0.55}
            envMap={envMap}
            envMapIntensity={HILT_ENV_INTENSITY}
          />
        </mesh>
        {/* Seppa-dai — raised centre boss, both faces */}
        <mesh scale={[1, 1, 0.22]}>
          <sphereGeometry args={[0.019, 10, 8]} />
          <meshStandardMaterial
            color="#3a3b32"
            metalness={0.6}
            roughness={0.5}
            envMap={envMap}
            envMapIntensity={HILT_ENV_INTENSITY}
          />
        </mesh>
      </group>

      {/* Fuchi — collar at the guard side of the handle */}
      <mesh
        position={[0, 0, FUCHI_Z]}
        rotation={[Math.PI / 2, 0, 0]}
        scale={[TSUKA_OVAL, 1, 1]}
      >
        <cylinderGeometry args={[0.0165, 0.0165, 0.014, 12]} />
        <meshStandardMaterial
          color="#20211f"
          metalness={0.65}
          roughness={0.45}
          envMap={envMap}
          envMapIntensity={HILT_ENV_INTENSITY}
        />
      </mesh>

      {/* Tsuka — handle, tsukamaki diamond wrap over rayskin (procedural
          texture, see hiltTextures.ts). Oval section (TSUKA_OVAL), slight
          taper toward the pommel. Silk/rayskin — no envMap. */}
      <mesh
        position={[0, 0, HANDLE_Z]}
        rotation={[Math.PI / 2, 0, 0]}
        scale={[TSUKA_OVAL, 1, 1]}
      >
        <cylinderGeometry args={[0.0165, 0.015, HANDLE_LENGTH, 12]} />
        <meshStandardMaterial map={tsukaWrapTexture} roughness={0.85} />
      </mesh>

      {/* Menuki — ornament peeking from beneath a wrap crossing */}
      <mesh
        position={[
          0.0175 * TSUKA_OVAL * Math.sin(MENUKI_ANGLE),
          0.0175 * Math.cos(MENUKI_ANGLE),
          MENUKI_Z,
        ]}
        rotation={[Math.PI / 2, 0, MENUKI_ANGLE]}
        scale={[1, 0.5, 0.6]}
      >
        <sphereGeometry args={[0.012, 8, 6]} />
        <meshStandardMaterial
          color={colors.gold}
          emissive={colors.gold}
          emissiveIntensity={0.3}
          metalness={0.8}
          roughness={0.35}
          envMap={envMap}
          envMapIntensity={MENUKI_ENV_INTENSITY}
        />
      </mesh>

      {/* Kashira — pommel cap */}
      <mesh position={[0, 0, KASHIRA_Z]} scale={[TSUKA_OVAL, 1, 0.5]}>
        <sphereGeometry args={[0.0175, 10, 8]} />
        <meshStandardMaterial
          color="#191a18"
          metalness={0.6}
          roughness={0.5}
          envMap={envMap}
          envMapIntensity={HILT_ENV_INTENSITY}
        />
      </mesh>

      {/* Blade base reference — near the habaki, reserved for projected shadow */}
      <group ref={baseRef} position={[0, 0, TRAIL_BASE_Z]} />
      {TRAIL_EDGE_SAMPLE_T.map((t, i) => (
        <group
          key={t}
          ref={trailPointRefs[i]}
          position={[0, trailEdgeYAt(t), bladeSceneZAt(t)]}
        />
      ))}

      {/* Blade tip — collision tracking + anchor for the ribbon trail, fake
          shadow, and combo aura below. Positioned at the visible kissaki
          (risen by SORI_RISE per the blade's sori curve). No separate thin
          trail primitive here — BladeTrail below is the sole trail, a wide
          additive ribbon; a second drei <Trail> layered on top produced its
          own independently-sampled thin line that drifted from the ribbon,
          reading as a pale offset "second outline" (see
          .extras/design/HANDOFF-trail-and-bloom-fix.md Part 1). */}
      <group ref={tipRef} position={[0, SORI_RISE, TIP_LOCAL_Z]} />

      {/* Edge-fit ribbon trail — speed-adaptive brightness */}
      <BladeTrail pointRefs={trailPointRefs} speedRef={speedRef} />

      {/* Combo aura — point light near tip, grows with combo, and briefly
          flashes violet on a tenouchi hit (see TENOUCHI_FLASH_* + useFrame
          above). Always mounted (intensity drops to 0 instead of
          unmounting) so NUM_POINT_LIGHTS stays constant as combo/flash
          rise and fall — mount/unmount here forces a scene-wide shader
          recompile on every threshold crossing, a stall source during
          dense phases. */}
      <pointLight
        ref={auraLightRef}
        position={[0, SORI_RISE, TIP_LOCAL_Z]}
        color={auraColor}
        intensity={auraIntensity}
        distance={1.2}
      />
    </group>
  );
}
